import AddIcon from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { AddOnRateType } from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FieldValues,
  useFieldArray,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import AddPhasesFromProjectDialog from '~components/Order/AddPhasesFromProjectDialog/AddPhasesFromProjectDialog';
import {
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { PhaseSelection } from '~components/Order/PhaseSelection';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import { ResourceUsageLogType } from '~constants/enums';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { NameIdSchemaRequired } from '~constants/regexConst';
import { AddOn, AddOnCharge } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { DriverBasic } from '~hooks/useDrivers/models';
import { BasicEquipment, EquipmentTypeBasic } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { useOrders } from '~hooks/useOrders/useOrders';
import { Phase, PhaseBasic } from '~hooks/useProjects/models';
import { ResourceUsageLog } from '~hooks/useResourceUsageLogs/models';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEquipmentLabel } from '~utils/equipment';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AddOnSelectOption } from '../../DriverPay/AddOnChargesTableRows';
import { appendAddOnRateTypeToName, schemaHelpers } from '../../DriverPay/helpers';

type Charge = {
  id: Nullable<string>;
  description: string;
  quantity: number;
  phase?: Nullable<Phase>;
  phaseLabel?: string;
  type?: ResourceUsageLogType;
};
interface ChargesTableProps {
  sx?: SxProps;
  isEditing: boolean;
  resourceUsageLogs: ResourceUsageLog[];
  addOns: AddOnCharge[];
  phasesOptions: PhaseBasic[];
  driver: DriverBasic;
  isJobLevel?: boolean;
  owningCompanyId?: string;
  hideCreatePhase?: boolean;
  orderId?: string;
  projectId?: string;
  reloadOrderPhases?: () => Promise<void>;
}

const deriveChargeFromResourceUsageLog = (resourceUsageLog: ResourceUsageLog): Charge => {
  return {
    id: resourceUsageLog.id ?? null,
    description: resourceUsageLog.equipment
      ? getEquipmentLabel(resourceUsageLog.equipment)
      : resourceUsageLog.driver
        ? `${resourceUsageLog.driver.fullName}${resourceUsageLog.driver.companyShare ? '-' + resourceUsageLog.driver.companyShare.senderCompany.legalName : ''}`
        : '',
    quantity: resourceUsageLog.usageMinutes / 60,
    phase: resourceUsageLog.phase,
    phaseLabel: resourceUsageLog.phase
      ? `${resourceUsageLog.phase.name + ' (' + resourceUsageLog.phase.code + ')'}`
      : '-',
    type: resourceUsageLog.equipment
      ? ResourceUsageLogType.EQUIPMENT
      : ResourceUsageLogType.DRIVER,
  };
};

const deriveChargeFromAddOn = (addon: AddOnCharge): Charge => {
  return {
    id: addon.id ?? '',
    description: `AddOn: ${addon.name}`,
    quantity:
      addon.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
        ? 0
        : addon.quantity,
    phaseLabel: '-',
  };
};

// Every row rendered while editing spans six cells: category, description, quantity,
// phase, a spacer, and the remove-button cell. A full-width row has to span all six.
const EDITING_ROW_COLUMN_COUNT = 6;

const TableRowStyled = styled(TableRow)<TableRowProps>(({ theme }) => ({
  '&:not(.MuiTableBody-root tr:last-child)': {
    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
  },
}));

const TableCellStyled = styled(TableCell, {
  shouldForwardProp: (propName) => propName !== 'isHeader',
})<TableCellProps & { isHeader?: boolean }>(({ isHeader, theme }) => ({
  borderBottom: 0,
  color: theme.brandV2.colors.treadBlack,
  fontSize: '12px',
  fontWeight: isHeader ? 600 : 400,
  padding: theme.spacing(),
  whiteSpace: 'nowrap',
}));

const ResourceUsageLogToCharges = ({
  resourceUsageLogs,
  isEditing,
  phasesOptions,
  fields,
  onAddPhasesFromOtherProject,
  update,
  remove,
}: {
  resourceUsageLogs: ResourceUsageLog[];
  isEditing: boolean;
  phasesOptions: PhaseBasic[];
  fields: FieldValues[];
  onAddPhasesFromOtherProject?: () => void;
  update: UseFieldArrayUpdate<FieldValues, 'resourceUsageLogs'>;
  remove: UseFieldArrayRemove;
}) => {
  const theme = useTheme();
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const { userStore } = useStores();
  const { getEquipmentByCompanyIdTypeahead } = useEquipment();

  const companyId = getEffectiveUserCompanyId(userStore);
  const watchedResourceUsageLogs = useWatch({ control, name: 'resourceUsageLogs' });

  const filteredPhaseOptionsPerRow = useMemo(() => {
    if (!watchedResourceUsageLogs) {
      return phasesOptions.map((phase) => ({
        id: phase.id,
        name: phase.name,
        code: phase.code,
      }));
    }

    return watchedResourceUsageLogs.map(
      (log: ResourceUsageLog & { _destroy?: number }, currentIndex: number) => {
        const equipmentId = log.equipment?.id;
        if (!equipmentId) {
          return phasesOptions.map((phase) => ({
            id: phase.id,
            name: phase.name,
            code: phase.code,
          }));
        }

        const usedPhaseIds = watchedResourceUsageLogs
          .filter(
            (otherLog: ResourceUsageLog & { _destroy?: number }, idx: number) =>
              idx !== currentIndex &&
              otherLog.equipment?.id === equipmentId &&
              otherLog.phase?.id &&
              !otherLog._destroy,
          )
          .map(
            (otherLog: ResourceUsageLog & { _destroy?: number }) => otherLog.phase!.id,
          );

        return phasesOptions
          .filter((phase) => !usedPhaseIds.includes(phase.id))
          .map((phase) => ({
            id: phase.id,
            name: phase.name,
            code: phase.code,
          }));
      },
    );
  }, [watchedResourceUsageLogs, phasesOptions]);

  const fetchEquipments = useCallback(
    async (additionalProps: { query?: string; cursor?: string } = {}) => {
      if (!companyId) {
        return { data: [], pagination: undefined };
      }

      const response = await getEquipmentByCompanyIdTypeahead({
        companyId,
        limit: DEFAULT_API_PAGE_SIZE,
        query: additionalProps.query || undefined,
        cursor: additionalProps.cursor,
      }).catch(() => {
        // Error is already handled in the hook
        return { data: [], pagination: undefined };
      });

      if (!response?.data) {
        return [];
      }

      const equipments = response.data.map((equipment) => {
        return { id: equipment.id, name: getEquipmentLabel(equipment) };
      });

      return { data: equipments, pagination: response.pagination };
    },
    [companyId],
  );

  const handleRemoveAddOnClick = useCallback(
    (index: number) => {
      const resourceUsageLog = watchedResourceUsageLogs?.[index];

      if (resourceUsageLog?.id) {
        update(index, { ...resourceUsageLog, _destroy: 1 });
      } else {
        remove(index);
      }
    },
    [update, watchedResourceUsageLogs],
  );

  const charges = useMemo(() => {
    return resourceUsageLogs.map((resourceUsageLog) => {
      return deriveChargeFromResourceUsageLog(resourceUsageLog);
    });
  }, [resourceUsageLogs]);

  if (isEditing) {
    return fields?.map((resource, index) => {
      const isDeleted = watchedResourceUsageLogs?.[index]?._destroy === 1;

      if (isDeleted) {
        return null;
      }
      const isEquipment =
        resourceUsageLogs[index]?.equipment?.id ||
        resource.type === ResourceUsageLogType.EQUIPMENT;

      const noAssignedEquipment =
        watchedResourceUsageLogs[index]?.type === 'equipment' &&
        !watchedResourceUsageLogs[index]?.equipment?.id;

      return (
        <TableRowStyled key={resource.id}>
          <TableCellStyled sx={{ pb: noAssignedEquipment ? 2.5 : 'auto' }}>
            {isEquipment ? 'Equipment' : 'Driver'}
          </TableCellStyled>

          <TableCellStyled sx={{ pb: noAssignedEquipment ? 2.5 : 'auto' }}>
            {!isEquipment ? (
              <OverflowAwareText>{resource.driver.name}</OverflowAwareText>
            ) : (
              <AutocompleteAsyncFormField
                asyncCallback={fetchEquipments}
                clearable
                control={control}
                errors={errors}
                getLabel={(item: NameIdSchemaRequired | null) => item?.name ?? ''}
                getValue={(item: NameIdSchemaRequired | null) => item?.id ?? ''}
                hideLabel
                isRequired
                name={`resourceUsageLogs[${index}].equipment`}
                onClear={() => {
                  setValue(`resourceUsageLogs[${index}].equipment`, { id: '', name: '' });
                }}
                sx={{
                  fontSize: '12px',
                  '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '5px !important',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 'fit-content',
                  },
                }}
              />
            )}
          </TableCellStyled>
          <TableCellStyled sx={{ pb: noAssignedEquipment ? 2.5 : 'auto' }}>
            <TextFormField
              control={control}
              errors={errors}
              name={`resourceUsageLogs[${index}].quantity`}
            />
          </TableCellStyled>
          <TableCellStyled sx={{ pb: noAssignedEquipment ? 2.5 : 'auto' }}>
            {phasesOptions.length > 0 ? (
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <PhaseSelection
                    phaseOptions={filteredPhaseOptionsPerRow[index] || []}
                    canEdit={false}
                    canAdd={false}
                    hasLabels={false}
                    selectedPhase={watchedResourceUsageLogs[index]?.phase}
                    onChange={(phase) => {
                      update(index, {
                        ...watchedResourceUsageLogs[index],
                        phase: phase,
                      });
                    }}
                    inputStyle={{
                      width: '100%',
                    }}
                    disableAutoDefaultedPhase
                    isDisabled={noAssignedEquipment}
                    {...(onAddPhasesFromOtherProject
                      ? { onAddPhasesFromOtherProject }
                      : {})}
                  />
                </Box>
                {noAssignedEquipment && (
                  <Typography variant="caption" sx={{ position: 'absolute' }}>
                    {t('approvals.driver_day.select_equipment_to_add_phase')}
                  </Typography>
                )}
              </Box>
            ) : (
              <OverflowAwareText>{charges[index]?.phaseLabel}</OverflowAwareText>
            )}
          </TableCellStyled>

          <TableCellStyled />
          <TableCellStyled
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <IconButton
              size={'medium'}
              onClick={() => {
                handleRemoveAddOnClick(index);
              }}
              sx={{
                justifyContent: 'flex-end',
                display: 'flex',
              }}
            >
              <Close
                fontSize={'medium'}
                sx={{ color: theme.brandV2.colors.treadGray3 }}
              />
            </IconButton>
          </TableCellStyled>
        </TableRowStyled>
      );
    });
  }

  return charges?.map((charge, index) => {
    const isEquipment = resourceUsageLogs[index].equipment?.id;
    return (
      <TableRowStyled key={charge.id}>
        <TableCellStyled>
          {' '}
          <OverflowAwareText>
            {isEquipment
              ? t('approvals.driver_pay.charges_table.equipment')
              : t('approvals.driver_pay.charges_table.driver')}{' '}
          </OverflowAwareText>
        </TableCellStyled>

        <TableCellStyled>
          <OverflowAwareText>{charge.description}</OverflowAwareText>
        </TableCellStyled>
        <TableCellStyled>
          <OverflowAwareText>{charge.quantity.toFixed(2)}</OverflowAwareText>
        </TableCellStyled>
        <TableCellStyled>
          <OverflowAwareText>{charge?.phaseLabel}</OverflowAwareText>
        </TableCellStyled>
      </TableRowStyled>
    );
  });
};

const AddOnsToCharges = ({
  addOns,
  isEditing,
  fields,
  update,
  remove,
}: {
  addOns: AddOnCharge[];
  isEditing: boolean;
  fields: FieldValues[];
  update: UseFieldArrayUpdate<FieldValues, 'addOnCharges'>;
  remove: UseFieldArrayRemove;
}) => {
  const theme = useTheme();
  const {
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const { addOnsStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });
  const previousWatchedAddOnCharges = usePrevious(watchedAddOnCharges);

  const { getAllAddOns } = useAddOns();

  const handleRemoveAddOnClick = useCallback(
    (index: number) => {
      const addOnChargeToDelete = watchedAddOnCharges?.[index];

      if (addOnChargeToDelete?.id) {
        update(index, { ...addOnChargeToDelete, _destroy: 1 });
      } else {
        remove(index);
      }
    },
    [update, watchedAddOnCharges],
  );

  const charges = useMemo(() => {
    return addOns.map((addOn) => {
      return deriveChargeFromAddOn(addOn);
    });
  }, [addOns]);

  const addOnSelectOptions = useMemo(() => {
    const options = addOnsStore.addOns.reduce((options, addOn) => {
      options.push({
        id: String(addOn.id),
        name: appendAddOnRateTypeToName(addOn.name, addOn.revenueCostRateType),
      });

      return options;
    }, [] as AddOnSelectOption[]);

    return options;
  }, [addOnsStore.addOns]);

  const addOnByAddOnId = useMemo(() => {
    const addOnByAddOnId = addOnsStore.addOns.reduce(
      (acc, addOn) => {
        acc[addOn.id ?? ''] = addOn;
        return acc;
      },
      {} as Record<string, AddOn>,
    );
    return addOnByAddOnId;
  }, [addOnsStore.addOns]);

  useEffect(() => {
    getAllAddOns(companyId);
  }, [companyId]);

  useEffect(() => {
    if (!watchedAddOnCharges || !previousWatchedAddOnCharges) {
      return;
    }
    watchedAddOnCharges.forEach(
      (current: { addOn: { id: any }; id: Nullable<string> }, index: number) => {
        const currentAddOnId = current.addOn?.id;
        const previousAddOnIn = previousWatchedAddOnCharges[index]?.addOn?.id;
        const selectedAddOnDidChange =
          currentAddOnId && currentAddOnId !== previousAddOnIn;
        // If the selected add-on did change then use the newly selected add-on values
        // As the default values for the add-on charge DTO
        if (selectedAddOnDidChange && addOnByAddOnId[currentAddOnId]) {
          const newlySelectedAddOn = addOnByAddOnId[currentAddOnId];
          const isNonRatePercentOfTotal =
            newlySelectedAddOn.revenueCostRateType === AddOnRateType.RATE_FOR_EACH ||
            newlySelectedAddOn.revenueCostRateType === AddOnRateType.PER_LOAD ||
            (newlySelectedAddOn.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
              !newlySelectedAddOn.percentage);
          const addOnCharge = new AddOnCharge(
            current.id,
            newlySelectedAddOn.name,
            newlySelectedAddOn.percentage,
            isNonRatePercentOfTotal ? newlySelectedAddOn.quantity : 0,
            newlySelectedAddOn.rate,
            0,
            newlySelectedAddOn.revenueCostRateType,
            newlySelectedAddOn.externalId,
            { id: currentAddOnId },
            null,
            null,
            null,
            null,
            null,
          );
          update(index, schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
          clearErrors([`addOnCharges.${index}.quantity`]);
          clearErrors([`addOnCharges.${index}.addOn`]);
        }
      },
    );
  }, [
    watchedAddOnCharges,
    previousWatchedAddOnCharges,
    update,
    clearErrors,
    addOnByAddOnId,
  ]);

  if (isEditing) {
    return (
      <>
        {fields.map((addOn, index) => {
          const isDeleted = watchedAddOnCharges?.[index]?._destroy === 1;

          if (isDeleted) {
            return null;
          }

          const editedAddOnCharge = watchedAddOnCharges?.[index];
          const isNonRatePercentOfTotal =
            editedAddOnCharge?.addOnRateType === AddOnRateType.RATE_FOR_EACH ||
            editedAddOnCharge?.addOnRateType === AddOnRateType.PER_LOAD ||
            (editedAddOnCharge?.addOnRateType === AddOnRateType.FUEL_SURCHARGE &&
              !editedAddOnCharge?.percentage);

          return (
            <TableRowStyled key={addOn.id}>
              <TableCellStyled>
                {' '}
                <OverflowAwareText>
                  {t('approvals.driver_pay.charges_table.add_on')}{' '}
                </OverflowAwareText>
              </TableCellStyled>
              <TableCellStyled>
                <AutocompleteFormField
                  clearable
                  control={control}
                  errors={errors}
                  getLabel={(item) => item?.name || ''}
                  getValue={(item) => item?.id || ''}
                  list={addOnSelectOptions}
                  name={`addOnCharges[${index}].addOn`}
                  sx={{
                    fontSize: '12px',
                    '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
                    '& .MuiAutocomplete-endAdornment': {
                      right: '5px !important',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 'fit-content',
                    },
                  }}
                />
              </TableCellStyled>
              <TableCellStyled>
                {isNonRatePercentOfTotal && (
                  <TextFormField
                    control={control}
                    errors={errors}
                    name={`addOnCharges[${index}].quantity`}
                  />
                )}
              </TableCellStyled>
              <TableCellStyled />
              <TableCellStyled />
              <TableCellStyled
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  size={'medium'}
                  onClick={() => handleRemoveAddOnClick(index)}
                  sx={{
                    justifyContent: 'flex-end',
                    display: 'flex',
                  }}
                >
                  <Close
                    fontSize={'medium'}
                    sx={{ color: theme.brandV2.colors.treadGray3 }}
                  />
                </IconButton>
              </TableCellStyled>
            </TableRowStyled>
          );
        })}
      </>
    );
  }

  return charges?.map((charge, index) => {
    const isNonRatePercentOfTotal =
      addOns[index].revenueCostRateType === AddOnRateType.RATE_FOR_EACH ||
      addOns[index].revenueCostRateType === AddOnRateType.PER_LOAD ||
      (addOns[index].revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
        !addOns[index].percentage);
    return (
      <TableRowStyled key={charge.id}>
        <TableCellStyled>
          {' '}
          <OverflowAwareText>
            {t('approvals.driver_pay.charges_table.add_on')}{' '}
          </OverflowAwareText>
        </TableCellStyled>

        <TableCellStyled>
          <OverflowAwareText>{charge.description}</OverflowAwareText>
        </TableCellStyled>
        <TableCellStyled>
          {isNonRatePercentOfTotal ? (
            <OverflowAwareText>{charge.quantity.toFixed(2)}</OverflowAwareText>
          ) : null}
        </TableCellStyled>
        <TableCellStyled>
          <OverflowAwareText>{charge?.phaseLabel}</OverflowAwareText>
        </TableCellStyled>
      </TableRowStyled>
    );
  });
};

export default function ChargesTable({
  sx,
  isEditing,
  resourceUsageLogs,
  addOns,
  phasesOptions,
  driver,
  isJobLevel = false,
  owningCompanyId,
  hideCreatePhase = false,
  orderId,
  projectId,
  reloadOrderPhases,
}: ChargesTableProps) {
  const theme = useTheme();
  // Deduplicate phasesOptions to ensure unique name and code combinations
  // Note: this shouldnt be an issue for new Orders since we don't allow duplicate phases anymore.
  const deduplicatedPhasesOptions = useMemo(() => {
    const seen = new Set<string>();
    return phasesOptions.filter((phase) => {
      const key = `${phase.name}|${phase.code}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [phasesOptions]);

  const showTableHeader = isEditing || resourceUsageLogs.length > 0 || addOns.length > 0;

  const [isAddPhasesDialogOpen, setIsAddPhasesDialogOpen] = useState(false);
  const [isCreatePhaseDialogOpen, setIsCreatePhaseDialogOpen] = useState(false);
  const [isCreatingPhase, setIsCreatingPhase] = useState(false);
  const [createPhaseError, setCreatePhaseError] = useState<string | null>(null);
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { addNewPhasesToOrder } = useOrders();

  const {
    control: phaseFormControl,
    formState: { isDirty, errors },
    watch: watchPhase,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      name: null,
      code: null,
    },
  });

  const watchedPhase = watchPhase();
  const { control } = useFormContext();
  const {
    fields: resourceLogsFields,
    append: appendResourceLog,
    update: updateResourceLog,
    remove: removeResourceLog,
  } = useFieldArray({
    control,
    name: 'resourceUsageLogs',
  });
  const watchedResourceUsageLogs = useWatch({ control, name: 'resourceUsageLogs' });
  const canAddPhasesFromOtherProject = isJobLevel && Boolean(orderId);
  const handleOpenAddPhasesDialog = useCallback(() => {
    setIsAddPhasesDialogOpen(true);
  }, []);
  const handleCloseAddPhasesDialog = useCallback(() => {
    setIsAddPhasesDialogOpen(false);
  }, []);

  const handleCloseCreatePhaseDialog = useCallback(() => {
    setIsCreatePhaseDialogOpen(false);
    reset();
    setCreatePhaseError(null);
  }, [reset]);

  const validateNewPhase = (name: string, code: string) => {
    const existingPhase = deduplicatedPhasesOptions.find(
      (phase) => phase.name === name && phase.code === code,
    );

    if (existingPhase) {
      return false;
    }

    return true;
  };

  const onCreatePhase = (data: any) => {
    if (orderId && reloadOrderPhases) {
      const isPhaseUnique = validateNewPhase(data.name, data.code);
      if (!isPhaseUnique) {
        setCreatePhaseError(t('approvals.driver_day.phase_is_not_unique'));
        return;
      }
      setIsCreatingPhase(true);

      addNewPhasesToOrder(companyId, orderId, [{ name: data.name, code: data.code }])
        .then(async () => {
          setIsCreatePhaseDialogOpen(false);
          reset();
          setCreatePhaseError(null);
          // Store current phases before reload
          const currentPhases = watchedResourceUsageLogs?.map((log: any) => log.phase);

          await reloadOrderPhases();
          // Restore phases to entries
          if (currentPhases?.length) {
            currentPhases.forEach((phase: any, index: number) => {
              if (phase && watchedResourceUsageLogs[index]) {
                updateResourceLog(index, { ...watchedResourceUsageLogs[index], phase });
              }
            });
          }
        })
        .finally(() => {
          setIsCreatingPhase(false);
        });
    }
  };

  const {
    fields: addOnFields,
    append: appendAddOn,
    update: updateAddOn,
    remove: removeAddOn,
  } = useFieldArray({
    control,
    name: 'addOnCharges',
  });

  const handleAddAddOnClick = useCallback(() => {
    const addOnCharge = new AddOnCharge(
      null,
      '',
      0,
      0,
      0,
      0,
      AddOnRateType.RATE_FOR_EACH,
      null,
      { id: '' },
      null,
      null,
      null,
      null,
      null,
    );
    appendAddOn(schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
  }, [appendAddOn]);

  const handleAddResourceLogOnClick = useCallback(
    (type: ResourceUsageLogType) => {
      const resourceUsageLog = new ResourceUsageLog(
        null,
        dayjs().tz(),
        dayjs().tz(),
        0,
        type === ResourceUsageLogType.EQUIPMENT
          ? new BasicEquipment(
              '', // Empty ID, will be selected by user
              '', // Empty name, will be selected by user
              '', // Empty externalId, will be selected by user
              new EquipmentTypeBasic(
                '', // Empty type ID, will be selected by user
                '', // Empty type name, will be selected by user
                false, // Empty type system, will be selected by user
              ),
              null,
              '', // Empty licenseNumber, will be selected by user
              '', // Empty accountingId, will be selected by user
              '', // Empty zone, will be selected by user
              '',
            )
          : null,
        driver,
        null,
        '',
        '',
      );

      appendResourceLog({
        ...schemaHelpers.resourceUsageLogToResourceUsageLogDTO(resourceUsageLog, type),
        type,
      });
    },
    [appendResourceLog, driver],
  );

  return (
    showTableHeader && (
      <TableContainer sx={{ px: 0.5, ...sx }}>
        <Table sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRowStyled>
              <TableCellStyled sx={{ width: '15%', fontWeight: 600 }}>
                {t('common.category')}
              </TableCellStyled>
              <TableCellStyled sx={{ width: '25%', fontWeight: 600 }}>
                {t('common.description')}
              </TableCellStyled>
              <TableCellStyled sx={{ width: '11%', fontWeight: 600 }}>
                {t('approvals.driver_day.add_on_charges_table.qty_header')}
              </TableCellStyled>
              <TableCellStyled sx={{ width: '42%', fontWeight: 600 }}>
                {isEditing ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <span style={{ flex: 1 }}>{t('form_fields.phase_name')}</span>
                    <span style={{ flex: 1 }}> {t('form_fields.phase_code')}</span>
                  </Box>
                ) : (
                  t('common.phase')
                )}
              </TableCellStyled>
            </TableRowStyled>
          </TableHead>

          <TableBody>
            <ResourceUsageLogToCharges
              resourceUsageLogs={resourceUsageLogs}
              isEditing={isEditing}
              phasesOptions={deduplicatedPhasesOptions}
              fields={resourceLogsFields}
              update={updateResourceLog}
              remove={removeResourceLog}
              {...(canAddPhasesFromOtherProject
                ? { onAddPhasesFromOtherProject: handleOpenAddPhasesDialog }
                : {})}
            />
            <AddOnsToCharges
              addOns={addOns}
              isEditing={isEditing}
              fields={addOnFields}
              update={updateAddOn}
              remove={removeAddOn}
            />
            {isEditing && (
              <TableRowStyled>
                <TableCellStyled
                  colSpan={EDITING_ROW_COLUMN_COUNT}
                  sx={{ p: 1.5, pl: 1 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    <SmallButton
                      color="brandV2Yellow"
                      sx={{
                        '&.MuiButtonBase-root': {
                          height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                        },
                      }}
                      onClick={() => {
                        handleAddAddOnClick();
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('approvals.driver_pay.settlements.add_on')}
                    </SmallButton>
                    <SmallButton
                      color="brandV2Black"
                      variant="outlined"
                      sx={{
                        // brandV2Black is a fixed navy (invisible on the dark-mode
                        // surface); drive the outlined label + border from flipping
                        // tokens so it reads in both modes (TRE-15890).
                        color: 'text.primary',
                        borderColor: 'divider',
                        '&.MuiButtonBase-root': {
                          height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                        },
                      }}
                      onClick={() => {
                        handleAddResourceLogOnClick(ResourceUsageLogType.DRIVER);
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('approvals.driver_day.driver_hours')}
                    </SmallButton>
                    <SmallButton
                      color="brandV2Black"
                      variant="outlined"
                      sx={{
                        color: 'text.primary',
                        borderColor: 'divider',
                        '&.MuiButtonBase-root': {
                          height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                        },
                      }}
                      onClick={() => {
                        handleAddResourceLogOnClick(ResourceUsageLogType.EQUIPMENT);
                      }}
                      startIcon={<AddIcon />}
                    >
                      {t('approvals.driver_day.equipment_hours')}
                    </SmallButton>
                    {isJobLevel && !hideCreatePhase ? (
                      <SmallButton
                        color="brandV2Black"
                        variant="outlined"
                        sx={{
                          color: 'text.primary',
                          borderColor: 'divider',
                          '&.MuiButtonBase-root': {
                            height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                          },
                        }}
                        onClick={() => {
                          setIsCreatePhaseDialogOpen(true);
                        }}
                      >
                        {t('approvals.driver_day.create_phase')}
                      </SmallButton>
                    ) : null}
                    {canAddPhasesFromOtherProject ? (
                      <SmallButton
                        color="brandV2Black"
                        data-test-id="add-phases-from-other-project-button"
                        onClick={handleOpenAddPhasesDialog}
                        sx={{
                          color: 'text.primary',
                          borderColor: 'divider',
                          '&.MuiButtonBase-root': {
                            height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
                          },
                        }}
                        variant="outlined"
                      >
                        {t('phases.add_from_other_project')}
                      </SmallButton>
                    ) : null}

                    <Dialog
                      isOpen={isCreatePhaseDialogOpen}
                      onClose={handleCloseCreatePhaseDialog}
                      preventCloseOnEscapeKeyDown={true}
                    >
                      <DialogHeader onClose={handleCloseCreatePhaseDialog}>
                        {t('approvals.driver_day.create_phase')}
                      </DialogHeader>
                      <DialogContent>
                        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextFormField
                              control={phaseFormControl}
                              name="name"
                              label={t('form_fields.phase_name')}
                              isRequired
                              errors={errors}
                              onFocus={() => {
                                setCreatePhaseError(null);
                              }}
                            />

                            <TextFormField
                              control={phaseFormControl}
                              name="code"
                              label={t('form_fields.phase_code')}
                              isRequired
                              errors={errors}
                              onFocus={() => {
                                setCreatePhaseError(null);
                              }}
                            />
                          </Box>
                          {createPhaseError && (
                            <Typography
                              variant="body2"
                              color={`${theme.brandV2.colors.treadRed}`}
                            >
                              {createPhaseError}
                            </Typography>
                          )}
                        </Box>
                      </DialogContent>
                      <DialogActions>
                        <SmallButton
                          variant="outlined"
                          color="secondary"
                          onClick={handleCloseCreatePhaseDialog}
                          disabled={isCreatingPhase}
                        >
                          {t('actions.cancel')}
                        </SmallButton>
                        <SmallLoadingButton
                          variant="contained"
                          color="primary"
                          loading={isCreatingPhase}
                          onClick={() => {
                            handleSubmit(onCreatePhase)();
                          }}
                          disabled={
                            !isDirty ||
                            !watchedPhase.name ||
                            !watchedPhase.code ||
                            Boolean(createPhaseError)
                          }
                        >
                          {t('actions.create')}
                        </SmallLoadingButton>
                      </DialogActions>
                    </Dialog>
                    {canAddPhasesFromOtherProject && orderId ? (
                      <AddPhasesFromProjectDialog
                        companyId={owningCompanyId ?? companyId}
                        currentProjectId={projectId}
                        existingPhases={phasesOptions}
                        isOpen={isAddPhasesDialogOpen}
                        onClose={handleCloseAddPhasesDialog}
                        onSuccess={() => Promise.resolve(reloadOrderPhases?.())}
                        orderId={orderId}
                      />
                    ) : null}
                  </Box>
                </TableCellStyled>
              </TableRowStyled>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  );
}
